import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowRight, Check } from "lucide-react";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { toast } from "sonner";
import { makeRequest } from "@/utils/makeRequest";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    setLoading(true);
    try {
      const res = await makeRequest.post("/auth/forgot-password", { email });

      if (res.data) {
        toast.success(res.data.message);
        setSubmitted(true);
      }
      setLoading(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
      console.log(error);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background px-4">
      <div className="w-full sm:max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F0B90B] to-[#E07B39] flex items-center justify-center mx-auto mb-4">
            <span className="text-black font-bold text-2xl">N</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Reset Password
          </h1>
          <p className="text-muted-foreground">
            Enter your email to receive reset instructions
          </p>
        </div>

        <div className="nex-card p-2 sm:p-4">
          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-10">
              <div>
                <Label className="text-base mb-2">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full !h-12 pl-11 pr-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || !email}
                className="w-full py-3 disabled:cursor-not-allowed disabled:opacity-40 bg-[#F0B90B] text-black font-semibold rounded-lg hover:brightness-110 transition-all flex items-center justify-center gap-2 !h-12"
              >
                {loading ? "Loading..." : " Send Reset Link"}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </form>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-[#0ECB81]/10 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-500" />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Check Your Email
              </h2>
              <p className="text-muted-foreground mb-6">
                We've sent password reset instructions to {email}
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                Back to Login
              </Link>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-center text-base text-muted-foreground flex items-center justify-between gap-2">
              <span>Remember your password?</span>
              <Link to="/login" className="text-primary hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
